import { selectOpenMatterArtifactId } from '@/matter-artifacts/store/selectors'
import { AgentV2UIStateContext } from '@/agent-v2/components/agent-v2-ui-state-context'
import { buildUIState } from '@/agent-v2/utils/ui-state'
import { TableTitlesProvider } from '@/tables/components/TableTitlesProvider'
import { TableNavigationContext } from '@/tables/components/TableNavigationContext'
import { useChatTable } from '@/tables/hooks/useChatTable'
import { useParams } from 'react-router-dom'
import clsx from 'clsx'
import { type ReactNode, useEffect, useMemo, useRef } from 'react'
import { CSSTransition } from 'react-transition-group'

import ContentViewerColumn from '@/content-viewer/components/ContentViewerColumn'
import ContentViewerConstructor from '@/content-viewer/components/ContentViewerConstructor'
import ContentViewerSlideover from '@/content-viewer/components/ContentViewerSlideover'
import { selectActiveContentViewerSession, selectIsContentViewerOpen } from '@/content-viewer/store/selectors'
import { useIsSmallChatBreakpoint } from '@/hooks/useIsSmallChatBreakpoint'
import ChatPanelHeader from '@/drafting/workspace/components/ChatPanelHeader'
import DraftPanel from '@/drafting/workspace/components/DraftPanel'
import FloatingAiAssistantButton from '@/drafting/workspace/components/FloatingAiAssistantButton'
import SourceChip from '@/drafting/workspace/components/SourceChip'
import { kChatPanelTabIds } from '@/drafting/workspace/chat-panel-tab-ids'
import { useChatPanelTabs } from '@/drafting/workspace/hooks/useChatPanelTabs'
import { useChatPanelToggleFocus } from '@/drafting/workspace/hooks/useChatPanelToggleFocus'
import { useDraftWorkspace } from '@/drafting/workspace/hooks/useDraftWorkspace'
import { useSelectSourceTabOnOpen } from '@/drafting/workspace/hooks/useSelectSourceTabOnOpen'
import { selectChatPanelOpen } from '@/drafting/workspace/store/selectors'
import { ChatPanelTabType } from '@/drafting/workspace/store/slice'
import { setVisibleChatConversationId } from '@/chat-common/store/chat-ux.slice'
import type { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import '@/drafting/workspace/components/chat-panel.css'

type AgentV2ChatLayoutProps = {
  /* The conversation the route resolved. */
  conversationId: string
  /* The chat window, passed in so that panel state changing here re-renders the arrangement but not the transcript. */
  children: ReactNode
}

/**
 * Arranges the chat against whatever sits beside it: a draft document with the chat docked alongside, a
 * content viewer column, or neither.
 *
 * Renders the chat in the same tree position in every arrangement, so that moving between them never tears
 * it down and rebuilds its transcript.
 */
export default function AgentV2ChatLayout({ conversationId, children }: AgentV2ChatLayoutProps) {
  const { matterId } = useParams()
  const { openTable, refreshKey } = useChatTable(conversationId)
  const isSmallScreen = useIsSmallChatBreakpoint()
  const { draftArtifactVersionId } = useDraftWorkspace()
  const showContentViewerPane = useAppSelector((state: RootState) => selectIsContentViewerOpen(state))
  const referenceColumnRef = useRef<HTMLDivElement | null>(null)
  const chatPanelRef = useRef<HTMLDivElement | null>(null)
  const chatPanelOpen = useAppSelector((state: RootState) => (draftArtifactVersionId ? selectChatPanelOpen(state, { draftArtifactVersionId }) : false))
  const { sourceSession, hasSourceTab, selectedTab } = useChatPanelTabs()

  const activeSourceSession = useAppSelector(selectActiveContentViewerSession)
  const artifactId = useAppSelector((state: RootState) => selectOpenMatterArtifactId(state, { draftArtifactVersionId }))
  const uiState = useMemo(() => buildUIState({ sourceSession: activeSourceSession, artifactId }), [activeSourceSession, artifactId])

  const isInDraftWorkspace = Boolean(draftArtifactVersionId)
  // The chat only docks beside the document on wider screens; on small screens the draft is the workspace's only surface.
  const chatPanelDocked = chatPanelOpen && !isSmallScreen
  const sourceTabSelected = hasSourceTab && selectedTab === ChatPanelTabType.Source
  // The chat stays mounted while a draft hides it; results that land in it then need telling elsewhere.
  const isChatVisible = !isInDraftWorkspace || (chatPanelDocked && !sourceTabSelected)
  const dispatch = useAppDispatch()
  useEffect(() => {
    if (!isChatVisible) return
    dispatch(setVisibleChatConversationId({ conversationId }))
    return () => {
      dispatch(setVisibleChatConversationId({ conversationId: null }))
    }
  }, [conversationId, dispatch, isChatVisible])

  const { openedWithoutToggleRef, handleSourcePanelKeyDown, openSourceTab } = useSelectSourceTabOnOpen()
  const { collapseButtonRef, reopenButtonRef } = useChatPanelToggleFocus({ draftArtifactVersionId, chatPanelOpen, isSmallScreen, openedWithoutToggleRef })
  // The chat is only a tab's target while a tab bar sits above it; elsewhere it is just the chat.
  const conversationTabContentProps = hasSourceTab
    ? { role: 'tabpanel', id: kChatPanelTabIds[ChatPanelTabType.Assistant].content, 'aria-labelledby': kChatPanelTabIds[ChatPanelTabType.Assistant].tab }
    : {}

  // The chat window uses one of three layouts, switched by className so the chat keeps its place in the tree and its
  // rendered transcript, rather than being torn down and rebuilt on every transition.
  let chatWindowClassName: string
  if (!isInDraftWorkspace) {
    chatWindowClassName = 'flex min-w-0 w-1/2 mx-0 lg:mx-0 flex-grow justify-center' // normal chat: centered main column
  } else if (isSmallScreen) {
    chatWindowClassName = 'hidden' // draft workspace on small screens: hidden (shows draft only), use floating button to get back to the chat
  } else {
    // Draft workspace on wider screens: docked beside the draft in a card that slides in when opened and out past the row's edge when collapsed.
    chatWindowClassName = clsx(
      'chat-panel-dock flex min-w-0 shrink-0 flex-col overflow-hidden rounded-lg border border-border-strong',
      // Collapsed is `invisible` (visibility:hidden), not opacity-0 alone, so keyboard focus and screen readers skip the hidden chat.
      chatPanelOpen ? 'chat-panel-expand opacity-100' : 'chat-panel-collapse opacity-0 invisible'
    )
  }

  return (
    <TableTitlesProvider key={matterId} refreshKey={refreshKey}>
      <TableNavigationContext.Provider value={matterId ? { matterId, openTable } : null}>
        <div
          className={clsx(
            'relative flex min-w-0 flex-grow',
            // Clips without making the row scrollable, so focusing inside the sliding panel cannot scroll the row sideways.
            isInDraftWorkspace && !isSmallScreen && 'overflow-x-clip',
            chatPanelDocked ? 'gap-x-4 p-4' : !isInDraftWorkspace && 'gap-x-2'
          )}
        >
          {draftArtifactVersionId && (
            <DraftPanel
              conversationId={conversationId}
              matterArtifactVersionId={draftArtifactVersionId}
              isSmallScreen={isSmallScreen}
              chatPanelRef={chatPanelRef}
            />
          )}

          <div ref={chatPanelRef} className={chatWindowClassName}>
            {/* Chat panel header that replaces the normal chat header when the chat sits in a card beside the draft document. */}
            {chatPanelDocked && <ChatPanelHeader key="chat-panel-header" conversationId={conversationId} collapseButtonRef={collapseButtonRef} />}
            <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              {/* Keyed so collapsing/reopening the panel never reparents and remounts the chat (ENG-7068). */}
              <div
                key="chat-body"
                {...conversationTabContentProps}
                className={clsx(
                  'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden',
                  chatPanelDocked && 'px-2',
                  // Hidden rather than unmounted, so the transcript keeps its scroll position while the source shows.
                  sourceTabSelected && 'invisible'
                )}
              >
                <AgentV2UIStateContext.Provider value={uiState}>{children}</AgentV2UIStateContext.Provider>
              </div>
              {/* Mounted as soon as a source is open, not when its tab is selected: nothing caches the document, so that
              would cost a fetch and a spinner. */}
              {hasSourceTab && (
                <div
                  role="tabpanel"
                  id={kChatPanelTabIds[ChatPanelTabType.Source].content}
                  aria-labelledby={kChatPanelTabIds[ChatPanelTabType.Source].tab}
                  onKeyDown={handleSourcePanelKeyDown}
                  className={clsx('absolute inset-0 flex flex-col overflow-hidden', !sourceTabSelected && 'invisible')}
                >
                  <ContentViewerConstructor conversationId={conversationId} headerVariant="source-tab" />
                </div>
              )}
            </div>
          </div>

          {draftArtifactVersionId && (
            <FloatingAiAssistantButton
              draftArtifactVersionId={draftArtifactVersionId}
              isSmallScreen={isSmallScreen}
              reopenButtonRef={reopenButtonRef}
              trailingContent={
                sourceSession && (
                  <SourceChip conversationId={conversationId} sourceSession={sourceSession} onOpen={openSourceTab} focusAfterCloseRef={reopenButtonRef} />
                )
              }
            />
          )}

          {/* The content viewer only shows outside the draft workspace: a slide-in column on desktop, a slideover on small screens. */}
          {!isInDraftWorkspace &&
            (isSmallScreen ? (
              <ContentViewerSlideover open={showContentViewerPane} conversationId={conversationId} />
            ) : (
              <CSSTransition
                appear
                nodeRef={referenceColumnRef}
                in={showContentViewerPane}
                timeout={{ enter: 200, exit: 0 }}
                classNames="content-viewer-column"
                unmountOnExit
              >
                <div ref={referenceColumnRef} className="w-1/2 min-w-0 flex flex-grow overflow-hidden">
                  <ContentViewerColumn conversationId={conversationId} />
                </div>
              </CSSTransition>
            ))}
        </div>
      </TableNavigationContext.Provider>
    </TableTitlesProvider>
  )
}
